/**
 * Codec do vetor de alcances (`contracts/authz/v1/golden/reach-encoding.golden.json`, design §2.4).
 *
 * Formato: 2 bits por índice de permissão do catálogo (00 none, 01 own, 10 unit, 11 org),
 * little-endian por byte (o índice de menor posição fica nos bits menos significativos do
 * byte), bytes concatenados na ordem dos índices, depois base64url SEM padding (`=`). Este é o
 * MESMO formato que `Azim.Authorization.PermissionClaimCodec` usa em C# (TASK-14) — os dois
 * lados são verificados contra os mesmos vetores de ouro, sem um depender do código do outro.
 *
 * Implementação em bytes puros (sem `Buffer`, que não existe em runtime de browser sem
 * polyfill) — este módulo roda tanto em Node (teste) quanto empacotado no bundle do portal.
 */
import type { Reach } from "./types.js";

export const REACH_CODE: Readonly<Record<Reach, number>> = {
  none: 0,
  own: 1,
  unit: 2,
  org: 3,
};

const CODE_TO_REACH: readonly Reach[] = ["none", "own", "unit", "org"];

const BASE64URL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";

function base64UrlEncode(bytes: Uint8Array): string {
  let output = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const b0 = bytes[i] as number;
    const b1 = bytes[i + 1] as number;
    const b2 = bytes[i + 2] as number;
    output += BASE64URL_ALPHABET[b0 >> 2];
    output += BASE64URL_ALPHABET[((b0 & 0x03) << 4) | (b1 >> 4)];
    output += BASE64URL_ALPHABET[((b1 & 0x0f) << 2) | (b2 >> 6)];
    output += BASE64URL_ALPHABET[b2 & 0x3f];
  }
  const remaining = bytes.length - i;
  if (remaining === 1) {
    const b0 = bytes[i] as number;
    output += BASE64URL_ALPHABET[b0 >> 2];
    output += BASE64URL_ALPHABET[(b0 & 0x03) << 4];
  } else if (remaining === 2) {
    const b0 = bytes[i] as number;
    const b1 = bytes[i + 1] as number;
    output += BASE64URL_ALPHABET[b0 >> 2];
    output += BASE64URL_ALPHABET[((b0 & 0x03) << 4) | (b1 >> 4)];
    output += BASE64URL_ALPHABET[(b1 & 0x0f) << 2];
  }
  return output;
}

const BASE64URL_REVERSE: Readonly<Record<string, number>> = (() => {
  const table: Record<string, number> = {};
  for (let i = 0; i < BASE64URL_ALPHABET.length; i += 1) {
    table[BASE64URL_ALPHABET[i] as string] = i;
  }
  return table;
})();

function base64UrlDecode(value: string): Uint8Array {
  const chars = value.split("").filter((c) => c !== "=");
  const bytes: number[] = [];
  for (let i = 0; i < chars.length; i += 4) {
    const c0 = BASE64URL_REVERSE[chars[i] as string] ?? 0;
    const c1 = i + 1 < chars.length ? BASE64URL_REVERSE[chars[i + 1] as string] ?? 0 : undefined;
    const c2 = i + 2 < chars.length ? BASE64URL_REVERSE[chars[i + 2] as string] ?? 0 : undefined;
    const c3 = i + 3 < chars.length ? BASE64URL_REVERSE[chars[i + 3] as string] ?? 0 : undefined;
    bytes.push(((c0 << 2) | ((c1 ?? 0) >> 4)) & 0xff);
    if (c2 !== undefined) bytes.push((((c1 ?? 0) & 0x0f) << 4) | (c2 >> 2));
    if (c3 !== undefined) bytes.push(((c2 ?? 0) & 0x03) << 6 | c3);
  }
  return Uint8Array.from(bytes);
}

/**
 * Codifica um vetor de alcances (denso ou esparso — índice ausente vale `"none"`) num string
 * base64url sem padding. `catalogSize` é o número de posições do vetor (não precisa ser o
 * tamanho real do catálogo — os vetores de ouro usam tamanhos sintéticos para testar o codec
 * isoladamente do catálogo real).
 */
export function encodeReachVector(
  reachesByIndex: Readonly<Record<number, Reach>>,
  catalogSize: number,
): string {
  if (catalogSize < 0 || !Number.isInteger(catalogSize)) {
    throw new RangeError(`catalogSize inválido: ${catalogSize}`);
  }
  const byteCount = Math.ceil(catalogSize / 4);
  const bytes = new Uint8Array(byteCount);
  for (let index = 0; index < catalogSize; index += 1) {
    const reach = reachesByIndex[index] ?? "none";
    const code = REACH_CODE[reach];
    const byteIndex = Math.floor(index / 4);
    const bitOffset = (index % 4) * 2;
    bytes[byteIndex] = ((bytes[byteIndex] as number) | (code << bitOffset)) & 0xff;
  }
  return base64UrlEncode(bytes);
}

/**
 * Decodifica um string base64url no vetor DENSO de alcances (uma entrada para cada índice de
 * `0` a `catalogSize - 1`, inclusive `"none"` explícito — nunca omite índice).
 */
export function decodeReachVector(
  base64url: string,
  catalogSize: number,
): Record<number, Reach> {
  if (catalogSize < 0 || !Number.isInteger(catalogSize)) {
    throw new RangeError(`catalogSize inválido: ${catalogSize}`);
  }
  const bytes = base64UrlDecode(base64url);
  const result: Record<number, Reach> = {};
  for (let index = 0; index < catalogSize; index += 1) {
    const byteIndex = Math.floor(index / 4);
    const bitOffset = (index % 4) * 2;
    const byte = bytes[byteIndex] ?? 0;
    const code = (byte >> bitOffset) & 0x03;
    result[index] = CODE_TO_REACH[code] as Reach;
  }
  return result;
}
